//#region Modules
import { ITaskRecord, TaskPriority, TaskRepeatingMethod, TaskStatus } from "@anydo/interfaces";
import type { ISyncLogic } from "../types/ISyncLogic";
import type { ITaskDto } from "../types/sync-types";
import { getSyncLogicSyncConfiguration } from "./-private/getSyncLogicSyncConfiguration";
//#endregion

//#region Helpers
function createField<T extends string>(
  field: T,
): {
  field: T;
  syncCounter: `${T}SyncCounter`;
  updateTime: `${T}UpdateTime`;
} {
  return {
    field,
    syncCounter: `${field}SyncCounter` as const,
    updateTime: `${field}UpdateTime` as const,
  };
}
//#endregion

export const TaskSyncLogic: ISyncLogic<"task"> = {
  name: "task",

  //#region fieldsConfiguration
  fieldsConfiguration: [
    createField("alert"),
    createField("categoryId"),
    createField("chatConversationId"),
    createField("dueDate"),
    createField("labels"),
    createField("note"),
    createField("position"),
    createField("priority"),
    createField("status"),
    createField("title"),
  ],
  //#endregion

  getDtoId(dto) {
    return dto.id;
  },

  mapDtoToModel(dto: ITaskDto, prevModel?: ITaskRecord): ITaskRecord {
    return {
      alert: dto.alert,
      appleReminderId: dto.appleReminderId ?? null,
      categoryId: dto.categoryId,
      chatConversationId: dto.chatConversationId,
      creationDate: dto.creationDate,
      dueDate: dto.dueDate,
      id: dto.id,
      labels: dto.labels,
      note: dto.note,
      parentGlobalTaskId: dto.parentGlobalTaskId,
      position: dto.position,
      priority: toTaskPriority(dto.priority),
      repeatingMethod: toRepeatingMethond(dto.repeatingMethod),
      status: toTaskStatus(dto.status),
      title: dto.title,

      // Update time
      alertUpdateTime: dto.alertUpdateTime,
      categoryIdUpdateTime: dto.categoryIdUpdateTime,
      chatConversationIdUpdateTime: dto.chatConversationIdUpdateTime,
      dueDateUpdateTime: dto.dueDateUpdateTime,
      labelsUpdateTime: dto.labelsUpdateTime,
      lastUpdateDate: dto.lastUpdateDate,
      noteUpdateTime: dto.noteUpdateTime,
      positionUpdateTime: dto.positionUpdateTime,
      priorityUpdateTime: dto.priorityUpdateTime,
      statusUpdateTime: dto.statusUpdateTime,
      titleUpdateTime: dto.titleUpdateTime,

      // Sync counter
      alertSyncCounter: prevModel == null ? null : prevModel.alertSyncCounter,
      categoryIdSyncCounter: prevModel == null ? null : prevModel.categoryIdSyncCounter,
      chatConversationIdSyncCounter: prevModel == null ? null : prevModel.chatConversationIdSyncCounter,
      dueDateSyncCounter: prevModel == null ? null : prevModel.dueDateSyncCounter,
      labelsSyncCounter: prevModel == null ? null : prevModel.labelsSyncCounter,
      noteSyncCounter: prevModel == null ? null : prevModel.noteSyncCounter,
      positionSyncCounter: prevModel == null ? null : prevModel.positionSyncCounter,
      prioritySyncCounter: prevModel == null ? null : prevModel.prioritySyncCounter,
      statusSyncCounter: prevModel == null ? null : prevModel.statusSyncCounter,
      titleSyncCounter: prevModel == null ? null : prevModel.titleSyncCounter,

      // Deprecated
      evernoteNotes: dto.evernoteNotes,
      globalTaskId: dto.globalTaskId,
      latitude: dto.latitude,
      longitude: dto.longitude,
      participants: dto.participants,
      subTasks: dto.subTasks,

      // Client properties
      dirty: 0,
      deleted: shouldRemoveModelFromStore(dto) ? 1 : 0,
    };
  },

  mapModelToDto(model: ITaskRecord): ITaskDto {
    return {
      alert: model.alert,
      appleReminderId: model.appleReminderId,
      categoryId: model.categoryId,
      chatConversationId: model.chatConversationId,
      creationDate: model.creationDate,
      dueDate: model.dueDate,
      id: model.id,
      labels: model.labels,
      note: model.note,
      parentGlobalTaskId: model.parentGlobalTaskId,
      position: model.position,
      priority: model.priority,
      repeatingMethod: model.repeatingMethod,
      status: model.status,
      title: model.title,

      // Update time
      alertUpdateTime: model.alertUpdateTime,
      categoryIdUpdateTime: model.categoryIdUpdateTime,
      chatConversationIdUpdateTime: model.chatConversationIdUpdateTime,
      dueDateUpdateTime: model.dueDateUpdateTime,
      labelsUpdateTime: model.labelsUpdateTime,
      lastUpdateDate: model.lastUpdateDate,
      noteUpdateTime: model.noteUpdateTime,
      positionUpdateTime: model.positionUpdateTime,
      priorityUpdateTime: model.priorityUpdateTime,
      statusUpdateTime: model.statusUpdateTime,
      titleUpdateTime: model.titleUpdateTime,

      // Deprecated
      evernoteNotes: model.evernoteNotes,
      globalTaskId: model.globalTaskId,
      latitude: model.latitude,
      longitude: model.longitude,
      participants: model.participants,
      subTasks: model.subTasks,
    };
  },

  shouldRemoveModelFromStore,

  getSyncConfiguration: getSyncLogicSyncConfiguration,
};

function shouldRemoveModelFromStore(model: ITaskRecord | ITaskDto): boolean {
  return !(model.status === TaskStatus.UNCHECKED || model.status === TaskStatus.CHECKED);
}

function toTaskPriority(priority: ITaskDto["priority"]): TaskPriority | null {
  switch (priority) {
    case "Dummy":
      return TaskPriority.DUMMY;
    case "High":
      return TaskPriority.HIGH;
    case "Low":
      return TaskPriority.LOW;
    case "Normal":
      return TaskPriority.NORMAL;

    default:
      let _: null = priority;
      return _;
  }
}

function toRepeatingMethond(method: ITaskDto["repeatingMethod"]): TaskRepeatingMethod | null {
  switch (method) {
    case "TASK_REPEAT_OFF":
      return TaskRepeatingMethod.TASK_REPEAT_OFF;

    case "TASK_REPEAT_YEAR":
      return TaskRepeatingMethod.TASK_REPEAT_YEAR;

    case "TASK_REPEAT_MONTH":
      return TaskRepeatingMethod.TASK_REPEAT_MONTH;

    case "TASK_REPEAT_WEEK":
      return TaskRepeatingMethod.TASK_REPEAT_WEEK;

    case "TASK_REPEAT_DAY":
      return TaskRepeatingMethod.TASK_REPEAT_DAY;

    default:
      let _: null = method;
      return _;
  }
}

function toTaskStatus(status: ITaskDto["status"]): TaskStatus {
  switch (status) {
    case "CHECKED":
      return TaskStatus.CHECKED;

    case "UNCHECKED":
      return TaskStatus.UNCHECKED;

    case "DELETED":
      return TaskStatus.DELETED;

    case "DONE":
      return TaskStatus.DONE;

    default:
      let _: never = status;
      return _;
  }
}
